import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

// Saves on blur or Enter rather than on a debounce: the board refetches after every mutation, and a
// mid-typing refetch would fight the input for its value. Text areas grow with their content, so
// the whole text is always on screen without a scroll inside the field.
export default function InlineText({ value, onSave, placeholder, isTextArea = false, rows = 3, className = "text-sm" }) {
  const [draft, setDraft] = useState(value || "");
  const ref = useRef(null);

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  // The drawer mounts the field before it is laid out, so a height measured once at mount comes out
  // as two rows. Measuring again whenever the width changes catches the moment it becomes visible.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !isTextArea) return;
    const fit = () => {
      el.style.height = "auto";
      el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
    };
    fit();
    let width = el.offsetWidth;
    const observer = new ResizeObserver(() => {
      if (el.offsetWidth === width) return;
      width = el.offsetWidth;
      fit();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [draft, isTextArea]);

  const save = () => {
    if (draft === (value || "")) return;
    onSave(draft);
  };

  const props = {
    ref,
    value: draft,
    placeholder,
    onChange: (e) => setDraft(e.target.value),
    onBlur: save,
    className: `${className} border border-gray-300 rounded-md px-2 py-1.5 w-full`,
  };

  if (isTextArea) return <textarea {...props} rows={rows} className={`${props.className} resize-none overflow-hidden`} />;
  return <input {...props} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} />;
}
